import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mandantenAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Check, ChevronRight, Droplets, HeartHandshake, ClipboardCheck,
  FlaskConical, Flag, Loader2, ArrowLeft, StickyNote,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/* Zinzino-Verkaufsmappe — geführter Leitfaden für die 20-minütige
   BalanceTest-Beratung, analog zur P1-Verkaufsmappe (/verkauf).

   Aufbau nach der FBC-5-Phasen-Struktur (Rahmen → Bedarf → Angebot →
   Abschluss), inhaltlich ausschließlich aus belegten Quellen: Landingpage-
   Wortlaut (EFSA-konforme Aussagen!), Mandanten-Config, FBC-Grundsätze.
   Gesundheitsbezogene Aussagen NUR in den hinterlegten Formulierungen —
   der Kasten in Schritt 2 ist wörtlich zu verwenden.

   Der Abschluss-Schritt schreibt das Ergebnis an den Termin
   (POST /api/termine/{id}/ergebnis) und legt die Folge-Aufgaben an:
   Test übergeben → „Ergebnis besprechen" (+21 Tage) und „Nachtest" (+120 Tage). */

const ROT = '#E60000';

const SCHRITTE = [
  { n: 1, label: 'Vorbereitung' },
  { n: 2, label: 'Prinzip erklären' },
  { n: 3, label: 'Bedarf' },
  { n: 4, label: 'Test & Abschluss' },
];

const fmtDatum = (d) => { const [y, m, t] = (d || '').split('-'); return t ? `${t}.${m}.${y}` : d; };

export default function ZinzinoMappePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const terminId = params.get('termin');

  const [schritt, setSchritt] = useState(1);
  const [termine, setTermine] = useState([]);
  const [termin, setTermin] = useState(null);
  const [notiz, setNotiz] = useState('');
  const [ziele, setZiele] = useState({ gewicht: false, darm: false });
  const [sende, setSende] = useState(false);
  const [fertig, setFertig] = useState(null);   // {status, follow_ups_angelegt}

  const laden = useCallback(async () => {
    try {
      const r = await mandantenAPI.termine({ mandant: 'zinzino', zeitraum: 'kommend' });
      const alle = r.data;
      setTermine(alle);
      if (terminId) setTermin(alle.find((t) => t.id === terminId) || null);
    } catch { toast.error('Termine konnten nicht geladen werden'); }
  }, [terminId]);
  useEffect(() => { laden(); }, [laden]);

  const abschliessen = async (ergebnis) => {
    if (!termin) return;
    setSende(true);
    try {
      const zielListe = [ziele.gewicht && 'Gewichtsreduktion', ziele.darm && 'Darmgesundheit'].filter(Boolean);
      const empfehlung = 'Bedarfsanalyse — Basis: BalanceTest + BalanceOil+'
        + (ziele.gewicht ? '; Ziel Gewichtsreduktion: ZinoBiotic+ und LeanShake empfohlen' : '')
        + (!ziele.gewicht && ziele.darm ? '; Ziel Darmgesundheit: ZinoBiotic+ empfohlen' : '')
        + (zielListe.length ? ` (Ziele: ${zielListe.join(', ')})` : '');
      const gesamtNotiz = [empfehlung, notiz.trim()].filter(Boolean).join(' — ');
      const r = await mandantenAPI.terminErgebnis(termin.id, { ergebnis, notiz: gesamtNotiz });
      setFertig(r.data);
      toast.success(`Ergebnis gespeichert: ${ergebnis}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSende(false);
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-5" data-testid="zinzino-mappe">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate('/mandanten/zinzino')}>
          <ArrowLeft className="h-4 w-4" /> Termine & Leads
        </Button>
        <h1 className="text-xl font-bold">Zinzino-Verkaufsmappe</h1>
        <Badge variant="outline">BalanceTest-Beratung · 20 Min</Badge>
      </div>

      {/* Termin wählen */}
      {!termin && !fertig && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Für welchen Termin?</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {termine.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Keine kommenden Zinzino-Termine. Die Mappe lässt sich auch ohne Termin
                durchblättern — Abschluss geht dann nicht.
              </p>
            )}
            {termine.map((t) => (
              <button key={t.id} onClick={() => setTermin(t)}
                      className="w-full flex items-center justify-between text-left text-sm border border-border rounded-lg px-3 py-2 hover:bg-muted/60">
                <span className="font-medium">{t.vorname} {t.nachname}</span>
                <span className="text-muted-foreground">{fmtDatum(t.datum)} · {t.uhrzeit} Uhr · {t.status}</span>
              </button>
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setTermin({ ohne: true })}>
              Ohne Termin durchblättern
            </Button>
          </CardContent>
        </Card>
      )}

      {termin && !fertig && (
        <>
          {!termin.ohne && (
            <div className="text-sm text-muted-foreground">
              Termin: <b className="text-foreground">{termin.vorname} {termin.nachname}</b> ·{' '}
              {fmtDatum(termin.datum)} um {termin.uhrzeit} Uhr · Status: {termin.status}
              {termin.herkunft?.utm_source && ` · Herkunft: ${termin.herkunft.utm_source}`}
            </div>
          )}

          {/* Schritt-Pillen (Muster der P1-Mappe) */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1">
            {SCHRITTE.map((s, i) => (
              <span key={s.n} className="flex items-center gap-1">
                <button onClick={() => s.n <= schritt && setSchritt(s.n)} disabled={s.n > schritt}
                        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors ${
                          s.n === schritt ? 'text-white' : s.n < schritt ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`}
                        style={s.n === schritt ? { background: ROT } : {}}>
                  {s.n < schritt ? <Check className="w-3.5 h-3.5" /> : <span>{s.n}</span>} {s.label}
                </button>
                {i < SCHRITTE.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-none" />}
              </span>
            ))}
          </div>

          {/* ── 1 · Vorbereitung / Rahmen ── */}
          {schritt === 1 && (
            <div className="space-y-4 max-w-3xl">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4" style={{ color: ROT }} /> Vor dem Gespräch (Gastgeber-Standard)
                </CardTitle></CardHeader>
                <CardContent className="text-sm space-y-1.5">
                  {['Testkit bereit und geprüft (Karte, Lanzetten, Versandumschlag)',
                    'Ruhiger Platz — kein Gespräch zwischen Tür und Angel',
                    'Herkunft des Kontakts angesehen (Flyer, Story, Empfehlung?)',
                    'Handy weg, 20 Minuten gehören dem Gast'].map((p) => (
                    <label key={p} className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" className="mt-1 accent-[#E60000]" /> <span>{p}</span>
                    </label>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Rahmen setzen (Phase 1)</CardTitle></CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                  <p>Zu Beginn den Rahmen klar machen — das nimmt Druck raus und ist ehrlich:</p>
                  <p className="border-l-2 pl-3 text-foreground" style={{ borderColor: ROT }}>
                    „Wir haben 20 Minuten. Ich zeige dir kurz, wie der BalanceTest funktioniert,
                    wir schauen auf deine Situation, und am Ende entscheidest du, ob der Test
                    für dich Sinn ergibt. Die Beratung kostet nichts — der Test selbst ist ein
                    Produkt und kostet extra, das besprechen wir dann gemeinsam."
                  </p>
                  <p>Kein Verkaufsdruck — Grundsatz: Es kommt regelmäßig vor, dass ein Test
                     nicht der richtige nächste Schritt ist. Dann sagen wir das.</p>
                </CardContent>
              </Card>
              <Button onClick={() => setSchritt(2)} style={{ background: ROT }} className="text-white">Weiter</Button>
            </div>
          )}

          {/* ── 2 · Prinzip erklären ── */}
          {schritt === 2 && (
            <div className="space-y-4 max-w-3xl">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
                  <Droplets className="h-4 w-4" style={{ color: ROT }} /> Das Prinzip in drei Schritten
                </CardTitle></CardHeader>
                <CardContent className="text-sm space-y-3">
                  <div><b>1 · Testen.</b> Vier Tropfen Blut aus der Fingerkuppe, unter zwei Minuten.
                    Ausgewertet bei Vitas in Oslo (unabhängig, GMP-zertifiziert, 25 Jahre
                    Fettsäureanalyse): elf Fettsäuren, sechs Indikatoren inkl. Omega-6:3-Verhältnis.
                    Ergebnis nach 10–21 Tagen als persönlicher Bericht, Benachrichtigung per SMS/E-Mail.</div>
                  <div><b>2 · Anpassen.</b> Mit dem Wert vor Augen wird über Ernährung und
                    Supplementierung entschieden — nach Zahl, nicht nach Gefühl.
                    Als ausgeglichen gilt ein Verhältnis unter 3:1.</div>
                  <div><b>3 · Nachtesten.</b> Nach 120 Tagen der zweite Test — erst der Vergleich
                    zeigt schwarz auf weiß, ob sich etwas bewegt hat.</div>
                </CardContent>
              </Card>
              <Card className="border-amber-300 dark:border-amber-700">
                <CardHeader className="pb-2"><CardTitle className="text-sm">⚠️ Gesundheitsaussagen — NUR diese Formulierungen</CardTitle></CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                  <p>DHA trägt zur Erhaltung normaler Gehirnfunktion und normaler Sehkraft bei
                     (250 mg DHA/Tag). EPA und DHA tragen zu einer normalen Herzfunktion bei
                     (250 mg EPA+DHA/Tag). Die mütterliche DHA-Aufnahme trägt zur normalen
                     Entwicklung von Gehirn und Augen beim Fötus und gestillten Säugling bei
                     (+200 mg DHA/Tag).</p>
                  <p className="text-foreground font-medium">Nie: Heilversprechen, Diagnosen, Aussagen zu Krankheiten,
                     Schmerzen oder Medikamenten. Bei Schwangeren/Stillenden: Messung ja,
                     Supplement-Fragen gehören zu Ärztin oder Hebamme.</p>
                </CardContent>
              </Card>
              <Button onClick={() => setSchritt(3)} style={{ background: ROT }} className="text-white">Weiter</Button>
            </div>
          )}

          {/* ── 3 · Bedarf ── */}
          {schritt === 3 && (
            <div className="space-y-4 max-w-3xl">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
                  <HeartHandshake className="h-4 w-4" style={{ color: ROT }} /> Bedarfsanalyse (Phase 2) — Gesprächsanker
                </CardTitle></CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                  <p>Offene Fragen, an der Lebenssituation orientiert — nicht dozieren, zuhören:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>„Was hat dich neugierig gemacht, deinen Wert zu kennen?" (Motiv)</li>
                    <li>„Wie sieht deine Woche aus — Fisch, Fertigprodukte, Öle?" (Ist-Zustand:
                        Omega-6 ist überall, Fisch zweimal die Woche schafft kaum jemand)</li>
                    <li>Lebensphase ansprechen, wenn passend: Kinderwunsch/Schwangerschaft/Stillzeit,
                        Training &amp; Regeneration, „wer trainiert, misst sonst alles — diese eine
                        Zahl kennt fast niemand"</li>
                    <li>„Angenommen, dein Wert wäre deutlich über 3:1 — würdest du etwas ändern
                        wollen?" (Macher-Frage nach FBC)</li>
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Ziele festhalten (steuert die Empfehlung in Schritt 4)</CardTitle></CardHeader>
                <CardContent className="text-sm space-y-2">
                  <p className="text-muted-foreground">
                    Reihenfolge ist fest: <b className="text-foreground">Zuerst wird der Omega-3-Wert
                    festgelegt</b> (BalanceTest) — er ist die Basis jeder Empfehlung. Danach kommen
                    die Ziele dazu:
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="accent-[#E60000]" checked={ziele.gewicht}
                           onChange={(e) => setZiele({ ...ziele, gewicht: e.target.checked })} />
                    <span><b>Gewichtsreduktion</b> ist ein Thema</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="accent-[#E60000]" checked={ziele.darm}
                           onChange={(e) => setZiele({ ...ziele, darm: e.target.checked })} />
                    <span><b>Darmgesundheit / Verdauung</b> ist ein Thema</span>
                  </label>
                </CardContent>
              </Card>
              <Button onClick={() => setSchritt(4)} style={{ background: ROT }} className="text-white">Weiter</Button>
            </div>
          )}

          {/* ── 4 · Test & Abschluss ── */}
          {schritt === 4 && (
            <div className="space-y-4 max-w-3xl">
              <Card style={{ borderColor: ROT }}>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Deine Empfehlung (aus der Bedarfsanalyse)</CardTitle></CardHeader>
                <CardContent className="text-sm space-y-3">
                  <div>
                    <p className="font-semibold">1 · Immer zuerst: BalanceTest + BalanceOil+</p>
                    <p className="text-muted-foreground">Der Omega-3-Wert wird festgelegt, bevor irgendetwas
                      anderes empfohlen wird — Test heute, Ergebnis in 10–21 Tagen, Nachtest nach 120 Tagen.</p>
                  </div>
                  {ziele.gewicht && (
                    <div>
                      <p className="font-semibold">2 · Ziel Gewichtsreduktion: ZinoBiotic+ und LeanShake</p>
                      <p className="text-muted-foreground">
                        <b className="text-foreground">ZinoBiotic+</b> — präbiotische Mischung aus acht natürlichen
                        Ballaststoffquellen (u. a. resistente Stärke, Beta-Glucane aus Hafer, Inulin,
                        Flohsamenschalen); trägt zur normalen Darmfunktion bei, Beta-Glucan trägt zu einem
                        normalen Cholesterinspiegel bei. 12 g Pulver auf 200 ml Wasser.<br />
                        <b className="text-foreground">LeanShake</b> — Mahlzeitenersatz zur Gewichtsregulierung
                        (Berry, Strawberry, Vanilla, Chocolate; 16 Portionen). Als Ersatz einzelner
                        Hauptmahlzeiten im Rahmen des Ziels einsetzen.
                      </p>
                    </div>
                  )}
                  {!ziele.gewicht && ziele.darm && (
                    <div>
                      <p className="font-semibold">2 · Ziel Darmgesundheit: ZinoBiotic+</p>
                      <p className="text-muted-foreground">Präbiotische Mischung aus acht natürlichen
                        Ballaststoffquellen — trägt zur normalen Darmfunktion bei, Beta-Glucan trägt zu
                        einem normalen Cholesterinspiegel bei. 12 g Pulver auf 200 ml Wasser.</p>
                    </div>
                  )}
                  {ziele.gewicht && ziele.darm && (
                    <p className="text-xs text-muted-foreground">Darmgesundheit ist mit ZinoBiotic+ bereits abgedeckt.</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Nur die zugelassenen Formulierungen verwenden — keine Abnehm-Versprechen über den
                    Mahlzeitenersatz-Rahmen hinaus, keine Aussagen zu Krankheiten. Preise erst hier im
                    Gespräch (Einzelkauf vs. Premier-Abo).
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
                  <FlaskConical className="h-4 w-4" style={{ color: ROT }} /> Angebot &amp; Preis (Phasen 3–4)
                </CardTitle></CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                  <p><b className="text-foreground">Wert vor Preis:</b> Erst zusammenfassen, was die Person davon hat
                     (ihr Ausgangswert, der Plan, der Nachtest als Beweis) — dann der Preis.
                     Der Test wird hier im Gespräch bepreist, nie vorher im Chat.</p>
                  <p>Bei Zusage: Test direkt vor Ort durchführen oder Kit mitgeben.
                     Nachtest-Logik erklären (120 Tage) — die Erinnerung übernimmt das CRM.</p>
                  <p>Bei Zögern: ehrlich bleiben. Kein Druck — lieber ein sauberes „diesmal nicht"
                     als ein bereuter Kauf. Folgetermin oder Bedenkzeit anbieten.</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
                  <Flag className="h-4 w-4" style={{ color: ROT }} /> Ergebnis festhalten
                </CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-2">
                    <StickyNote className="h-4 w-4 mt-2 text-muted-foreground flex-none" />
                    <textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2}
                              placeholder="Notiz zum Gespräch (optional) — landet am Termin"
                              className="w-full text-sm rounded-md border border-border bg-background p-2" />
                  </div>
                  {termin.ohne ? (
                    <p className="text-sm text-muted-foreground">Ohne ausgewählten Termin kann kein Ergebnis gespeichert werden.</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-2">
                      <Button disabled={sende} onClick={() => abschliessen('test_uebergeben')}
                              style={{ background: ROT }} className="text-white">
                        {sende ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test übergeben'}
                      </Button>
                      <Button disabled={sende} variant="outline" onClick={() => abschliessen('kunde')}>
                        Kunde geworden
                      </Button>
                      <Button disabled={sende} variant="outline" onClick={() => abschliessen('wahrgenommen')}>
                        Wahrgenommen, kein Test
                      </Button>
                      <Button disabled={sende} variant="ghost" onClick={() => abschliessen('nicht_erschienen')}>
                        Nicht erschienen
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    „Test übergeben" legt automatisch zwei Aufgaben an: Ergebnis besprechen
                    (+3 Wochen, wenn der Laborbericht da ist) und Nachtest fällig (+120 Tage).
                    „Nicht erschienen" legt eine Nachfass-Aufgabe für morgen an.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Fertig */}
      {fertig && (
        <Card className="max-w-3xl">
          <CardContent className="py-8 text-center space-y-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <Check className="h-6 w-6" />
            </span>
            <p className="font-bold text-lg">Ergebnis gespeichert: {fertig.status}</p>
            {fertig.follow_ups_angelegt > 0 && (
              <p className="text-sm text-muted-foreground">
                {fertig.follow_ups_angelegt} Folge-Aufgabe{fertig.follow_ups_angelegt > 1 ? 'n' : ''} angelegt — zu finden unter Follow-ups.
              </p>
            )}
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" onClick={() => { setFertig(null); setTermin(null); setSchritt(1); setNotiz(''); laden(); }}>
                Nächster Termin
              </Button>
              <Button onClick={() => navigate('/mandanten/zinzino')} style={{ background: ROT }} className="text-white">
                Zu Termine &amp; Leads
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
